import { reactive, watch, type Ref } from 'vue';
import type { useQuoteStore } from '@/stores/quoteStore';
import type { PersonalData } from '@/types/quote.types';

type QuoteStore = ReturnType<typeof useQuoteStore>;

/** Postal code option emitted by the vehicle step. */
export interface PostalCodeItem {
  code: string;
  displayValue: string;
  idEstado?: number;
  idCiudad?: number;
  idMunicipio?: number;
}

/** Driver form (gender stays empty until explicitly selected when Chubb is on). */
const createDriverFormState = () =>
  reactive({
    fullName: '',
    gender: '' as 'M' | 'F' | '',
    postalCode: '',
    postalCodeDisplay: '',
    birthDate: '',
    email: '',
    whatsapp: '',
    idEstado: undefined as number | undefined,
    idCiudad: undefined as number | undefined,
    idMunicipio: undefined as number | undefined,
    privacyConsent: false as boolean,
  });

export type DriverFormState = ReturnType<typeof createDriverFormState>;

const text = (value?: string): string => value || '';

const loadPersonalData = (form: DriverFormState, personalData: PersonalData): void => {
  Object.assign(form, {
    fullName: text(personalData.fullName),
    gender: personalData.gender || '',
    postalCode: text(personalData.postalCode),
    postalCodeDisplay: text(personalData.postalCode),
    birthDate: text(personalData.birthDate),
    email: text(personalData.email),
    whatsapp: text(personalData.whatsapp),
    idEstado: personalData.idEstado,
    idCiudad: personalData.idCiudad,
    idMunicipio: personalData.idMunicipio,
    privacyConsent: personalData.privacyConsent || false,
  });
};

const applyPostalCode = (form: DriverFormState, item: PostalCodeItem): void => {
  form.postalCode = item.code;
  form.postalCodeDisplay = item.displayValue;
  if (item.idEstado !== undefined) form.idEstado = item.idEstado;
  if (item.idCiudad !== undefined) form.idCiudad = item.idCiudad;
  if (item.idMunicipio !== undefined) form.idMunicipio = item.idMunicipio;
};

/** Driver form state, its handlers and the sync from the store when the form is shown. */
export const useDriverForm = (quoteStore: QuoteStore, showPersonalData: Ref<boolean>) => {
  const formData = createDriverFormState();

  const fillFromStore = (): void => {
    const personalData = quoteStore.quoteData.personalData;
    if (personalData) {
      loadPersonalData(formData, personalData);
    }
  };

  const handleFormUpdate = (field: string, value: unknown): void => {
    (formData as Record<string, unknown>)[field] = value;
  };

  const handlePostalCodeSelect = (item: PostalCodeItem): void => applyPostalCode(formData, item);

  watch(
    () => showPersonalData.value,
    (isShowing) => {
      if (isShowing) fillFromStore();
    },
  );

  return { formData, fillFromStore, handleFormUpdate, handlePostalCodeSelect };
};
